import React from 'react';
import {
    Card, CardContent, Typography, Alert, TextField, Box,
    Button, Stack, Avatar, InputAdornment, useTheme, Zoom
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';

const MemberSearchCard = ({
    activeTeamId,
    searchQuery,
    setSearchQuery,
    searchData,
    membersData,
    inviteLoading,
    handleInviteMember,
    inviteData,
    inviteErr
}) => {
    const theme = useTheme();

    return (
        <Card sx={{
            borderRadius: "12px",
            border: '1px solid',
            borderColor: 'divider',
            background: 'rgba(255,255,255,0.01)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'none',
            overflow: 'visible'
        }}>
            <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Box sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: 'rgba(190, 242, 100, 0.1)',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <PersonAddRoundedIcon fontSize="medium" />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                            Kader <span style={{ color: theme.palette.primary.main }}>Erweitern</span>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Suche nach registrierten Nutzern
                        </Typography>
                    </Box>
                </Stack>

                {!activeTeamId && (
                    <Alert
                        severity="warning"
                        sx={{
                            mb: 3,
                            borderRadius: "12px",
                            fontWeight: 700,
                            border: '1px solid',
                            borderColor: 'warning.main',
                            bgcolor: 'rgba(237, 108, 2, 0.05)'
                        }}
                    >
                        Bitte wähle zuerst ein aktives Team aus.
                    </Alert>
                )}

                <TextField
                    fullWidth
                    placeholder="Vorname, Nachname oder E-Mail"
                    disabled={!activeTeamId}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: 'primary.main' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: "12px",
                            bgcolor: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.01)',
                            fontWeight: 700,
                            transition: 'all 0.3s ease',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                            '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.05)' }
                        }
                    }}
                />

                {searchData?.users?.length > 0 && (
                    <Stack spacing={1.5}>
                        {searchData.users
                            .filter(u => !membersData?.members?.find(m => m.id === u.id))
                            .slice(0, 5)
                            .map((u, idx) => (
                                <Zoom in={true} key={u.id} style={{ transitionDelay: `${idx * 50}ms` }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2,
                                        bgcolor: 'rgba(255,255,255,0.02)',
                                        borderRadius: "12px",
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(190, 242, 100, 0.02)'
                                        }
                                    }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: 'primary.main', fontWeight: 900 }}>
                                                {u.first_name[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                    {u.first_name} {u.last_name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                    {u.email}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<GroupAddRoundedIcon />}
                                            onClick={() => handleInviteMember(u.id)}
                                            disabled={inviteLoading || !activeTeamId}
                                            sx={{
                                                borderRadius: "12px",
                                                fontWeight: 900,
                                                textTransform: 'none',
                                                bgcolor: 'primary.main',
                                                color: '#000',
                                                px: 2,
                                                '&:hover': { bgcolor: 'primary.dark' }
                                            }}
                                        >
                                            Hinzufügen
                                        </Button>
                                    </Box>
                                </Zoom>
                            ))}
                    </Stack>
                )}

                {searchQuery.length > 1 && searchData?.users?.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                        Keine Nutzer mit diesen Kriterien gefunden.
                    </Typography>
                )}

                {inviteData?.success && (
                    <Alert
                        severity="success"
                        sx={{
                            mt: 3,
                            borderRadius: "12px",
                            fontWeight: 800,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            bgcolor: 'rgba(190, 242, 100, 0.05)',
                            color: 'primary.main',
                            '& .MuiAlert-icon': { color: 'primary.main' }
                        }}
                    >
                        Erfolgreich zum Team hinzugefügt!
                    </Alert>
                )}
                {inviteErr && (
                    <Alert severity="error" sx={{ mt: 3, borderRadius: "12px", fontWeight: 700 }}>
                        {inviteErr}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export default MemberSearchCard;
